import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft, Play, FileText, CheckCircle2, XCircle, AlertCircle, 
  Clock, Award, RefreshCw, Video, Download, Lock, Timer,
  ChevronDown, ChevronUp, Maximize2
} from 'lucide-react';
import { toast } from 'sonner';
import { toEmbedUrl } from '../../utils/videoEmbed';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PortalCurso = () => {
  const params = useParams();
  const navigate = useNavigate();
  const cursoId = params.cursoId;
  
  const [curso, setCurso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('contenido');
  const [videoWatched, setVideoWatched] = useState(false);
  const [respuestas, setRespuestas] = useState({});
  const [evaluacionEnviada, setEvaluacionEnviada] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [tiempoRestante, setTiempoRestante] = useState(null);
  const [evaluacionIniciada, setEvaluacionIniciada] = useState(false);

  useEffect(function() {
    axios.get(API + '/trabajador/curso/' + cursoId)
      .then(function(res) {
        setCurso(res.data);
        if (res.data.registro && res.data.registro.video_completado) {
          setVideoWatched(true);
        }
        if (res.data.registro && res.data.registro.aprobado) {
          setEvaluacionEnviada(true);
          setResultado({ nota: res.data.registro.nota, aprobado: res.data.registro.aprobado });
        }
        setLoading(false);
      })
      .catch(function() {
        toast.error('Error al cargar el curso');
        navigate('/portal/dashboard');
      });
  }, [cursoId, navigate]);

  // Timer para la evaluación
  useEffect(() => {
    if (evaluacionIniciada && tiempoRestante > 0) {
      const timer = setInterval(() => {
        setTiempoRestante(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleSubmitEvaluacion();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [evaluacionIniciada, tiempoRestante]);

  function marcarVideoVisto() {
    axios.post(API + '/trabajador/curso/' + cursoId + '/video-completado')
      .then(function() {
        setVideoWatched(true);
        toast.success('Video marcado como visto. Ahora puedes realizar la evaluación.');
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || 'Error al marcar video');
      });
  }

  function iniciarEvaluacion() {
    if (curso.tiempo_evaluacion_minutos) {
      setTiempoRestante(curso.tiempo_evaluacion_minutos * 60);
    }
    setEvaluacionIniciada(true);
    setActiveTab('evaluacion');
  }

  function handleSubmitEvaluacion() {
    if (submitting) return;
    setSubmitting(true);
    
    axios.post(API + '/trabajador/curso/' + cursoId + '/evaluar', { respuestas: respuestas })
      .then(function(res) {
        setResultado(res.data);
        setEvaluacionEnviada(true);
        setEvaluacionIniciada(false);
        if (res.data.aprobado) {
          toast.success('¡Felicidades! Has aprobado el curso');
        } else {
          toast.error('No has alcanzado el puntaje mínimo. Intentos restantes: ' + res.data.intentos_restantes);
        }
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || 'Error al enviar evaluación');
      })
      .finally(function() {
        setSubmitting(false);
      });
  }

  function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  if (loading) {
    return (
      <div className="flex justify-center h-64 items-center">
        <div className="animate-spin h-8 w-8 border-b-2 border-teal-500 rounded-full"></div>
      </div>
    );
  }

  if (!curso) return null;

  const videos = (curso.videos && curso.videos.length > 0)
    ? curso.videos
    : (curso.video_url ? [{ id: 'main', titulo: curso.nombre || 'Video del curso', url: curso.video_url }] : []);
  const archivos = curso.archivos || [];
  const preguntas = curso.preguntas || [];
  const tieneVideos = videos.length > 0;
  const tieneArchivos = archivos.length > 0;
  const tieneEvaluacion = preguntas.length > 0;
  const puedeEvaluar = true;
  const estaBloqueado = curso.registro?.bloqueado;
  const intentosUsados = curso.registro?.intentos || 0;
  const intentosMax = curso.intentos_maximos || 3;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/portal/dashboard">
          <Button variant="ghost" size="icon" className="rounded-full h-8 w-8">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">{curso.nombre}</h1>
          <p className="text-sm text-slate-500">{curso.descripcion}</p>
        </div>
      </div>

      {/* Info del curso compacta */}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="px-2 py-1 bg-slate-100 rounded-full text-slate-600">
          <Clock className="w-3 h-3 inline mr-1" />
          {curso.horas || '-'}h
        </span>
        <span className={`px-2 py-1 rounded-full ${
          curso.modalidad === 'e-learning' ? 'bg-blue-100 text-blue-700' :
          curso.modalidad === 'presencial' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
        }`}>
          {curso.modalidad}
        </span>
        <span className="px-2 py-1 bg-slate-100 rounded-full text-slate-600">
          Aprobación: {curso.porcentaje_aprobacion}%
        </span>
        {curso.tiempo_evaluacion_minutos && (
          <span className="px-2 py-1 bg-amber-100 rounded-full text-amber-700">
            <Timer className="w-3 h-3 inline mr-1" />
            {curso.tiempo_evaluacion_minutos} min evaluación
          </span>
        )}
        <span className="px-2 py-1 bg-slate-100 rounded-full text-slate-600">
          {intentosMax} intentos máx.
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        <button 
          onClick={() => setActiveTab('contenido')} 
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'contenido' ? 'border-teal-500 text-teal-600' : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Contenido
        </button>
        {tieneEvaluacion && (
          <button 
            onClick={() => puedeEvaluar && !estaBloqueado && setActiveTab('evaluacion')} 
            disabled={!puedeEvaluar || estaBloqueado}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'evaluacion' ? 'border-teal-500 text-teal-600' : 
              (!puedeEvaluar || estaBloqueado) ? 'border-transparent text-slate-300 cursor-not-allowed' :
              'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Evaluación ({preguntas.length})
            {!puedeEvaluar && <Lock className="w-3 h-3 inline ml-1" />}
          </button>
        )}
      </div>

      {/* Contenido */}
      {activeTab === 'contenido' && (
        <div className="space-y-4">
          {/* Videos */}
          {tieneVideos && (
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Video className="w-4 h-4 text-blue-500" />
                  Videos del Curso ({videos.length})
                  {videoWatched && <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto" />}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {videos.map((video, idx) => (
                  <div key={video.id} className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedVideo(expandedVideo === video.id ? null : video.id)}
                      className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <Play className="w-4 h-4 text-blue-500" />
                        {video.titulo}
                        {video.duracion_minutos && (
                          <span className="text-xs text-slate-400">({video.duracion_minutos} min)</span>
                        )}
                      </span>
                      {expandedVideo === video.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    
                    {expandedVideo === video.id && (
                      <div className="p-3 bg-slate-100 flex justify-center">
                        <div className="w-[320px] h-[180px] bg-black rounded-lg overflow-hidden">
                          <iframe 
                            src={toEmbedUrl(video.url)} 
                            className="w-full h-full"
                            allowFullScreen 
                            title={video.titulo}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                {!videoWatched && (
                  <Button onClick={marcarVideoVisto} className="w-full bg-amber-500 hover:bg-amber-600">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    He visto todos los videos
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Archivos */}
          {tieneArchivos && (
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-red-500" />
                  Material de Apoyo ({archivos.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {archivos.map((archivo) => (
                  <a
                    key={archivo.id}
                    href={archivo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-lg border transition-colors"
                  >
                    <FileText className={`w-5 h-5 ${
                      archivo.tipo === 'pdf' ? 'text-red-500' : 'text-blue-500'
                    }`} />
                    <span className="flex-1 text-sm font-medium">{archivo.titulo}</span>
                    <Download className="w-4 h-4 text-slate-400" />
                  </a>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Sin contenido */}
          {!tieneVideos && !tieneArchivos && (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-6 text-center">
                <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                <h3 className="font-medium text-slate-900 mb-1">Sin contenido multimedia</h3>
                <p className="text-sm text-slate-600">
                  {tieneEvaluacion ? 'Dirígete a la pestaña Evaluación para rendir la prueba.' : 'Este curso no tiene contenido digital.'}
                </p>
                {tieneEvaluacion && !estaBloqueado && (
                  <Button onClick={iniciarEvaluacion} className="mt-4 bg-teal-500 hover:bg-teal-600">
                    Ir a Evaluación
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Botón para ir a evaluación */}
          {tieneEvaluacion && puedeEvaluar && !evaluacionEnviada && !estaBloqueado && (
            <Button onClick={iniciarEvaluacion} className="w-full bg-teal-500 hover:bg-teal-600">
              <Play className="w-4 h-4 mr-2" />
              Iniciar Evaluación
            </Button>
          )}
        </div>
      )}

      {/* Evaluación */}
      {activeTab === 'evaluacion' && (
        <div className="space-y-4">
          {/* Timer */}
          {evaluacionIniciada && tiempoRestante !== null && (
            <div className={`flex items-center justify-between p-3 rounded-lg ${
              tiempoRestante < 60 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
            }`}>
              <span className="flex items-center gap-2 font-medium">
                <Timer className="w-4 h-4" />
                Tiempo restante
              </span>
              <span className="text-lg font-bold">{formatTime(tiempoRestante)}</span>
            </div>
          )}

          {/* Bloqueado */}
          {estaBloqueado && (
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-6 text-center">
                <Lock className="w-10 h-10 text-red-500 mx-auto mb-3" />
                <h3 className="font-bold text-red-700 mb-2">Evaluación Bloqueada</h3>
                <p className="text-sm text-red-600">
                  Has agotado los {intentosMax} intentos permitidos para esta evaluación.
                  Contacta a tu supervisor.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Resultado */}
          {resultado && (
            <Card className={resultado.aprobado ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {resultado.aprobado ? (
                    <Award className="w-10 h-10 text-emerald-500" />
                  ) : (
                    <XCircle className="w-10 h-10 text-red-500" />
                  )}
                  <div className="flex-1">
                    <h3 className={`font-bold ${resultado.aprobado ? 'text-emerald-700' : 'text-red-700'}`}>
                      {resultado.aprobado ? '¡Aprobado!' : 'No Aprobado'}
                    </h3>
                    <p className="text-sm text-slate-600">
                      Nota: {resultado.nota?.toFixed(1)}% | Requerido: {curso.porcentaje_aprobacion}%
                      {!resultado.aprobado && resultado.intentos_restantes !== undefined && (
                        <span className="ml-2">| Intentos restantes: {resultado.intentos_restantes}</span>
                      )}
                    </p>
                  </div>
                  {resultado.aprobado && (
                    <Link to="/portal/dashboard">
                      <Button className="bg-emerald-500 hover:bg-emerald-600">Volver</Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Preguntas */}
          {!estaBloqueado && (!resultado || !resultado.aprobado) && preguntas.map((pregunta, idx) => (
            <Card key={pregunta.id} className="border">
              <CardContent className="p-4">
                <p className="font-medium text-sm text-slate-900 mb-3">
                  {idx + 1}. {pregunta.pregunta}
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {['A', 'B', 'C', 'D'].map((letra) => {
                    const alternativa = pregunta[`alternativa_${letra.toLowerCase()}`];
                    if (!alternativa) return null;
                    const isSelected = respuestas[pregunta.id] === letra;
                    
                    return (
                      <button
                        key={letra}
                        onClick={() => setRespuestas({ ...respuestas, [pregunta.id]: letra })}
                        disabled={evaluacionEnviada && resultado?.aprobado}
                        className={`text-left p-2 rounded border text-sm transition-colors ${
                          isSelected 
                            ? 'bg-teal-100 border-teal-400 text-teal-800' 
                            : 'bg-white hover:bg-slate-50 border-slate-200'
                        }`}
                      >
                        <span className="font-medium mr-2">{letra}.</span>
                        {alternativa}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Botón enviar */}
          {!estaBloqueado && (!resultado || !resultado.aprobado) && (
            <div className="flex gap-3">
              <Button
                onClick={handleSubmitEvaluacion}
                disabled={submitting || Object.keys(respuestas).length < preguntas.length}
                className="flex-1 bg-teal-500 hover:bg-teal-600 disabled:bg-slate-300"
              >
                {submitting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Enviar Evaluación ({Object.keys(respuestas).length}/{preguntas.length})
              </Button>
            </div>
          )}

          {/* Info de intentos */}
          {!estaBloqueado && intentosUsados > 0 && !resultado?.aprobado && (
            <p className="text-center text-sm text-slate-500">
              Intentos utilizados: {intentosUsados} de {intentosMax}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
